import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { Send, CheckCircle, AlertCircle, RefreshCw, Key, Trash2, Megaphone } from 'lucide-react';
import DashboardModuleShell from '../../components/DashboardModuleShell';
import { Button } from '../../components/ui/Button';

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-[#E5E5E5]">
        <Icon size={16} className="text-[#0176D3]" />
        <span className="text-[13px] font-bold text-[#2E2E2E]">{title}</span>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

const INPUT = 'w-full border border-[#E5E5E5] rounded-[4px] px-3 py-2 text-[13px] text-[#2E2E2E] focus:outline-none focus:border-[#0176D3]';

export default function TelegramSettingsPage() {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [botToken, setBotToken] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [channelId, setChannelId] = useState('');
  const [savingChannel, setSavingChannel] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/telegram/account');
      setAccount(data.success ? data.account : null);
    } catch {
      setAccount(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    if (!botToken.trim()) return;
    setConnecting(true);
    try {
      const { data } = await api.post('/telegram/connect', { botToken: botToken.trim() });
      setAccount(data.account);
      setBotToken('');
      alertService.toast('success', `Connected @${data.account.botUsername}`);
      if (!data.account.webhookRegistered) {
        alertService.toast('error', data.account.webhookRegisterError || 'Bot connected, but webhook registration failed — messages may not arrive yet.');
      }
    } catch (err) {
      alertService.error('Connection Failed', err.response?.data?.error || 'Could not connect this bot.');
    } finally {
      setConnecting(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      await api.post('/telegram/test');
      alertService.toast('success', 'Bot token and webhook are working correctly.');
      load();
    } catch (err) {
      alertService.error('Test Failed', err.response?.data?.error || 'Connection test failed.');
    } finally {
      setTesting(false);
    }
  };

  const saveBroadcastChannel = async () => {
    if (!channelId.trim()) return;
    setSavingChannel(true);
    try {
      const { data } = await api.put('/telegram/broadcast-channel', { chatId: channelId.trim() });
      setAccount((a) => ({ ...a, broadcastChannelId: data.broadcastChannelId, broadcastChannelTitle: data.broadcastChannelTitle }));
      setChannelId('');
      alertService.toast('success', `Cross-posting will publish to "${data.broadcastChannelTitle}"`);
    } catch (err) {
      alertService.error('Failed', err.response?.data?.error || 'Could not set broadcast channel — make sure the bot is an admin there.');
    } finally {
      setSavingChannel(false);
    }
  };

  const removeBroadcastChannel = async () => {
    try {
      await api.delete('/telegram/broadcast-channel');
      setAccount((a) => ({ ...a, broadcastChannelId: null, broadcastChannelTitle: null }));
      alertService.toast('success', 'Broadcast channel removed');
    } catch (err) {
      alertService.error('Failed', err.response?.data?.error || 'Could not remove broadcast channel.');
    }
  };

  const disconnect = async () => {
    if (!(await alertService.confirm('Disconnect?', 'Disconnect this Telegram bot? Existing conversations stay in your inbox, but no new messages will arrive.', 'danger', 'Disconnect'))) return;
    setDisconnecting(true);
    try {
      await api.post('/telegram/disconnect');
      setAccount(null);
      alertService.toast('success', 'Telegram bot disconnected');
    } catch (err) {
      alertService.error('Failed', err.response?.data?.error || 'Could not disconnect.');
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <DashboardModuleShell title="Telegram — Connect & Settings" subtitle="Connect your own Telegram bot (created via @BotFather) to receive and reply to messages in your CRM inbox.">
      {loading ? (
        <div className="text-center py-16 text-[13px] text-[#5C5C5C]">Loading…</div>
      ) : account ? (
        <Section title="Connected Bot" icon={CheckCircle}>
          <div className="flex items-center justify-between p-4 bg-[#F4F6F9] border border-[#E5E5E5] rounded-[4px]">
            <div>
              <p className="text-[14px] font-bold text-[#2E2E2E]">@{account.botUsername}</p>
              <p className="text-[12px] text-[#5C5C5C]">{account.botName}</p>
            </div>
            <span className={`text-[11px] font-bold px-2 py-1 rounded-full ${account.status === 'active' ? 'bg-[#E3F3E8] text-[#2E844A]' : 'bg-[#FDE7E9] text-[#8E030F]'}`}>
              {account.status === 'active' ? 'Active' : 'Needs Attention'}
            </span>
          </div>
          {!account.webhookRegistered && (
            <div className="flex items-start gap-2 p-3 bg-[#FFF4E5] border border-[#FFB75D] rounded-[4px] text-[12px] text-[#8C4B02]">
              <AlertCircle size={15} className="shrink-0 mt-0.5" />
              <span>Webhook registration failed{account.webhookRegisterError ? `: ${account.webhookRegisterError}` : ''} — messages from Telegram may not arrive. Try Test Connection below.</span>
            </div>
          )}
          <div className="flex gap-2 pt-2 border-t border-[#E5E5E5]">
            <Button variant="outline" onClick={test} loading={testing}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Test Connection
            </Button>
            <Button variant="outline" onClick={disconnect} loading={disconnecting} className="text-[#BA0517] border-[#F6A3AB] hover:bg-[#FDE7E9]">
              <Trash2 className="w-4 h-4 mr-2" />
              Disconnect
            </Button>
          </div>
        </Section>
      ) : null}

      {!loading && account && (
        <Section title="Cross-Posting Channel" icon={Megaphone}>
          <p className="text-[12px] text-[#5C5C5C]">
            Add this bot as an admin (with permission to post) to a channel or group, then paste its chat id here to
            include Telegram as a target in Content Calendar cross-posts.
          </p>
          {account.broadcastChannelId ? (
            <div className="flex items-center justify-between p-4 bg-[#F4F6F9] border border-[#E5E5E5] rounded-[4px]">
              <div>
                <p className="text-[14px] font-bold text-[#2E2E2E]">{account.broadcastChannelTitle}</p>
                <p className="text-[12px] text-[#5C5C5C]">{account.broadcastChannelId}</p>
              </div>
              <Button variant="outline" onClick={removeBroadcastChannel} className="text-[#BA0517] border-[#F6A3AB] hover:bg-[#FDE7E9]">
                <Trash2 className="w-4 h-4 mr-2" />
                Remove
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                className={INPUT}
                value={channelId}
                onChange={(e) => setChannelId(e.target.value)}
                placeholder="e.g. -1001234567890"
              />
              <Button onClick={saveBroadcastChannel} loading={savingChannel} disabled={!channelId.trim()}>Save</Button>
            </div>
          )}
        </Section>
      )}

      {!loading && !account && (
        <Section title="Connect a Bot" icon={Send}>
          <ol className="text-[12px] text-[#5C5C5C] space-y-1 list-decimal list-inside">
            <li>Open Telegram and message <strong>@BotFather</strong></li>
            <li>Send <code className="bg-[#F4F6F9] px-1 rounded">/newbot</code> and follow the prompts (or reuse an existing bot)</li>
            <li>Copy the token @BotFather gives you and paste it below</li>
          </ol>
          <div className="space-y-1.5">
            <label className="text-[11px] font-semibold text-[#706E6B] block">Bot Token</label>
            <input
              type="password"
              className={INPUT}
              value={botToken}
              onChange={(e) => setBotToken(e.target.value)}
              placeholder="123456789:AAExampleTokenFromBotFather"
            />
          </div>
          <Button onClick={connect} loading={connecting} disabled={!botToken.trim()}>
            <Key className="w-4 h-4 mr-2" />
            Connect Bot
          </Button>
        </Section>
      )}
    </DashboardModuleShell>
  );
}
